import { router, usePage } from '@inertiajs/vue3'
import { computed, readonly, ref, watch, type ComputedRef, type Ref } from 'vue'

import * as impersonationRoutes from '@/wayfinder/routes/portal/impersonation'

/**
 * Target identity type definition
 */
export interface ImpersonationTarget {
  id: string | number
  type: string
  name?: string
  email?: string
}

/**
 * State type definition
 */
export interface ImpersonationState {
  isImpersonating: boolean
  target: ImpersonationTarget | null
  /** The real logged-in account (the impersonator). Null when not impersonating. */
  impersonator?: ImpersonationTarget | null
  canImpersonate: boolean
}

/**
 * Module-level shared state (singleton pattern)
 */
const state = ref<ImpersonationState>({
  isImpersonating: false,
  target: null,
  impersonator: null,
  canImpersonate: false,
})

const isLoading = ref(false)

/**
 * Track if the watcher has been set up (to avoid duplicate watchers)
 */
let watcherInitialized = false

/**
 * Shared composable providing reactive impersonation state and actions.
 *
 * Uses module-level state for cross-component synchronization.
 * Automatically syncs state from page props when they change.
 */
export function useImpersonation() {
  const page = usePage()

  /**
   * Initialize state from Inertia props
   */
  const init = (serverState: Partial<ImpersonationState>): void => {
    state.value = {
      isImpersonating: serverState.isImpersonating ?? false,
      target: serverState.target ?? null,
      impersonator: serverState.impersonator ?? null,
      canImpersonate: serverState.canImpersonate ?? false,
    }
  }

  /**
   * Initialize from page props automatically
   */
  const initFromPage = (): void => {
    const impersonation = page.props.impersonation as ImpersonationState | undefined

    if (impersonation) {
      init(impersonation)
    }
  }

  /**
   * Set up a watcher to automatically sync state when page props change.
   * This ensures state is always in sync after Inertia navigation.
   */
  if (!watcherInitialized) {
    watcherInitialized = true

    // Watch for changes to impersonation props and sync state
    watch(
      () => page.props.impersonation as ImpersonationState | undefined,
      (impersonation) => {
        if (impersonation) {
          init(impersonation)
        }
      },
      { immediate: true, deep: true }
    )
  }

  /**
   * Start impersonation via Inertia router
   */
  const start = async (
    targetId: string | number,
    targetType: string = 'user',
    reason?: string
  ): Promise<void> => {
    if (isLoading.value) return

    isLoading.value = true

    return new Promise((resolve, reject) => {
      router.post(
        impersonationRoutes.start.url(),
        {
          target_id: String(targetId),
          target_type: targetType,
          reason: reason ?? null,
        },
        {
          preserveState: false,
          preserveScroll: false,
          onSuccess: () => {
            isLoading.value = false
            resolve()
          },
          onError: (errors) => {
            isLoading.value = false
            reject(errors)
          },
          onFinish: () => {
            isLoading.value = false
          },
        }
      )
    })
  }

  /**
   * Stop impersonation via Inertia router
   */
  const stop = async (): Promise<void> => {
    if (isLoading.value) return

    isLoading.value = true

    return new Promise((resolve, reject) => {
      router.post(
        impersonationRoutes.stop.url(),
        {},
        {
          preserveState: false,
          preserveScroll: false,
          onSuccess: () => {
            state.value = {
              isImpersonating: false,
              target: null,
              impersonator: null,
              canImpersonate: state.value.canImpersonate,
            }
            isLoading.value = false
            resolve()
          },
          onError: (errors) => {
            isLoading.value = false
            reject(errors)
          },
          onFinish: () => {
            isLoading.value = false
          },
        }
      )
    })
  }

  /**
   * Throttled call to server to refresh activity (debounced)
   */
  let refreshTimeout: ReturnType<typeof setTimeout> | null = null
  const refreshActivity = (): void => {
    if (refreshTimeout) return

    refreshTimeout = setTimeout(() => {
      refreshTimeout = null

      if (state.value.isImpersonating) {
        // Make a lightweight request to refresh activity timestamp
        fetch(impersonationRoutes.status.url(), {
          method: 'GET',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
          },
        }).catch(() => {
          // Silently ignore errors
        })
      }
    }, 30000) // Throttle to every 30 seconds
  }

  return {
    // Computed state (readonly)
    isImpersonating: computed(() => state.value.isImpersonating) as ComputedRef<boolean>,
    target: computed(() => state.value.target) as ComputedRef<ImpersonationTarget | null>,
    impersonator: computed(() => state.value.impersonator ?? null) as ComputedRef<ImpersonationTarget | null>,
    canImpersonate: computed(() => state.value.canImpersonate) as ComputedRef<boolean>,
    isLoading: readonly(isLoading) as Readonly<Ref<boolean>>,

    // Actions
    init,
    initFromPage,
    start,
    stop,
    refreshActivity,
  }
}
